import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { customerAuthApi, apiFetch } from '../lib/api';
import AuthShell from '../components/AuthShell';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { setTokens, setCustomer } = useAuthStore();

  const [providers, setProviders] = useState<string[]>([]);

  // Which buttons to draw is the API's answer, not a build-time constant: the
  // credentials live in system settings and can be turned on without a deploy.
  useEffect(() => {
    apiFetch<{ providers: string[] }>('/customer/auth/social/providers')
      .then((res) => setProviders((res.data as any)?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  // Coming back from a provider: the callback put a short-lived handoff in the
  // URL rather than the tokens themselves, so nothing usable is left in
  // browser history or a referrer header.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const handoff = params.get('social_handoff');
    const socialError = params.get('social_error');

    if (socialError) {
      setError(t(`auth.social_${socialError}`, { defaultValue: t('auth.socialFailed') }));
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (!handoff) return;

    setLoading(true);
    apiFetch<any>('/customer/auth/social/exchange', {
      method: 'POST',
      body: JSON.stringify({ handoff }),
    })
      .then((res) => {
        const data = (res as any).data;
        setTokens(data.accessToken, data.refreshToken);
        setCustomer(data.customer);
        navigate('/dashboard');
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => {
        setLoading(false);
        window.history.replaceState({}, '', window.location.pathname);
      });
  }, []);

  const startSocial = async (provider: string) => {
    setError('');
    try {
      const res = await apiFetch<{ url: string }>(`/customer/auth/social/${provider}/start`);
      window.location.href = (res.data as any).url;
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await customerAuthApi.login(email, password);
      
      if (response.data) {
        setTokens(response.data.accessToken, response.data.refreshToken);
        setCustomer(response.data.customer);
        navigate('/dashboard');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t('auth.loginTitle')}>
      {providers.length > 0 && (
        <div className="mb-6 space-y-2">
          {providers.includes('google') && (
            <button
              type="button"
              onClick={() => startSocial('google')}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
                <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/>
                <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.14 6.16-4.14Z"/>
              </svg>
              {t('auth.continueWithGoogle')}
            </button>
          )}
          {providers.includes('facebook') && (
            <button
              type="button"
              onClick={() => startSocial('facebook')}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
                <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96H15.83c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z"/>
              </svg>
              {t('auth.continueWithFacebook')}
            </button>
          )}
          <div className="flex items-center gap-3 pt-2">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs text-gray-400">{t('auth.orWithEmail')}</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
        </div>
      )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.emailLbl')}
            </label>
            <input
              id="email"
              type="email"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.passwordLbl')}
            </label>
            <input
              id="password"
              type="password"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700 focus:ring-4 focus:ring-brand-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <Link to="/forgot-password" className="text-sm text-brand-600 hover:text-brand-700">{t('auth.forgotLink')}</Link>
        </div>


        <p className="text-center text-sm text-gray-500 mt-6">
          {t('auth.needHelp')}
        </p>
    </AuthShell>
  );
}
